/* eslint-disable react/display-name */
import {
  Flex,
  Box,
  Button,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Checkbox,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
  flexRender,
} from '@tanstack/react-table';
import React, { useMemo, useState, useReducer } from 'react';

import { GetAssignedReceivablesForLoanProductQueryGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  frozenHeaderRowStyling,
  frozenTableStyling,
} from 'packages/common-frontend/styles';
import { useSlugToName } from '~/packages/dataPipeline/hooks/slugToNameContext';

const constructColumnDefinition = ({
  columnId,
  headerText,
  accessorFn,
}: {
  columnId: string;
  headerText: string;
  accessorFn: (row: any) => string;
}): ColumnDef<any, any> => {
  return {
    id: columnId,
    header: () => {
      return (
        <Typography variant="base" fontWeight="medium">
          {headerText}
        </Typography>
      );
    },
    accessorFn,
    cell: ({ getValue }) => {
      return <Typography variant="base-muted">{getValue()}</Typography>;
    },
    meta: {
      style: { padding: '16px' },
    },
  };
};

export const InitiateRepurchaseButton = ({
  onConfirm,
  rowsSelectedCount,
  jobInProgress,
}: {
  onConfirm: () => void;
  rowsSelectedCount: number;
  jobInProgress: boolean;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef(null);
  return (
    <>
      <Button
        variant="outline"
        colorScheme="aurora"
        leftIcon={
          rowsSelectedCount > 0 ? (
            <Box
              background={colors.gray[100]}
              borderRadius="4px"
              padding="4px 8px"
            >
              {rowsSelectedCount}
            </Box>
          ) : undefined
        }
        onClick={onOpen}
        isDisabled={rowsSelectedCount === 0 || jobInProgress}
        width="fit-content"
      >
        Repurchase
      </Button>
      <AlertDialog
        leastDestructiveRef={cancelRef}
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader
              fontSize="24px"
              fontWeight="bold"
              p="40px 40px 16px"
            >
              Confirm Repurchase
            </AlertDialogHeader>

            <AlertDialogBody p="8px 40px 16px">
              <Typography variant="base-muted">
                Are you sure you want to repurchase the selected receivables and
                move them to the balance sheet?
              </Typography>
            </AlertDialogBody>

            <AlertDialogFooter p="16px 40px 40px">
              <Button
                variant="outline"
                colorScheme="aurora"
                ref={cancelRef.current}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                colorScheme="aurora"
                onClick={async () => {
                  onConfirm();
                  onClose();
                }}
                ml="32px"
              >
                Confirm Repurchase
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export const ReceivableReconciliationTable = ({
  receivablesData,
  creditFacilitySlug,
  initiateRepurchaseConfirmationJob,
  jobInProgress,
}: {
  receivablesData: GetAssignedReceivablesForLoanProductQueryGQLType;
  creditFacilitySlug: string;
  initiateRepurchaseConfirmationJob: ({
    rowSelectionData,
  }: {
    rowSelectionData: any;
  }) => void;
  jobInProgress: boolean;
}) => {
  const { cfSlugToName } = useSlugToName();

  const columns: ColumnDef<any, any>[] = [
    constructColumnDefinition({
      columnId: 'externalReceivableId',
      headerText: 'Receivable ID',
      accessorFn: (row) => row.externalReceivableId,
    }),
    constructColumnDefinition({
      columnId: 'signaledCreditFacilityName',
      headerText: 'Loan Tape Assigned Facility',
      accessorFn: (row) => row.creditFacility.name,
    }),
    constructColumnDefinition({
      columnId: 'assignedCreditFacilityName',
      headerText: 'Finley Assigned Facility',
      accessorFn: () => cfSlugToName(creditFacilitySlug),
    }),
  ];

  const [rowsSelectedCount, setRowsSelectedCount] = useState<number>(0);

  const rowSelectionReducer = (state: any, action: any) => {
    switch (action.type) {
      case 'TOGGLE_ROW': {
        const newState = { ...state };
        setRowsSelectedCount(
          (oldCount) => oldCount + (state[action.rowId] ? -1 : 1),
        );
        if (newState[action.rowId]) {
          delete newState[action.rowId];
        } else {
          newState[action.rowId] = true;
        }
        return newState;
      }
      case 'TOGGLE_ALL': {
        if (
          rowsSelectedCount <
          receivablesData.assignedReceivablesForLoanProduct.length
        ) {
          setRowsSelectedCount(
            receivablesData.assignedReceivablesForLoanProduct.length,
          );
          return action.allRowIds.reduce(
            (acc: { [id: string]: boolean }, rowId: string) => {
              // eslint-disable-next-line no-param-reassign
              acc[rowId] = true;

              return acc;
            },
            {},
          );
        }
        setRowsSelectedCount(0);
        return {};
      }

      default:
        return state;
    }
  };

  const [rowSelection, dispatchRowSelection] = useReducer(
    rowSelectionReducer,
    {},
  );

  const table = useReactTable({
    data: receivablesData.assignedReceivablesForLoanProduct,
    columns,
    state: {
      rowSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    getRowId: (row) => row.externalReceivableId,
    onRowSelectionChange: dispatchRowSelection,
  });

  const allRowIds = useMemo(
    () => table.getRowModel().rows.map((row) => row.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [receivablesData.assignedReceivablesForLoanProduct],
  );

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center">
        <InitiateRepurchaseButton
          rowsSelectedCount={rowsSelectedCount}
          jobInProgress={jobInProgress}
          onConfirm={() => {
            initiateRepurchaseConfirmationJob({
              rowSelectionData: rowSelection,
            });
          }}
        />
        <Typography fontWeight="bold">
          Receivable Count: {allRowIds.length}
        </Typography>
      </Flex>
      <Box
        borderWidth="1px"
        borderColor={colors.gray[100]}
        overflowX="auto"
        overflowY="auto"
        position="relative"
        maxHeight="50vh"
        m="24px 0px"
      >
        <Table style={frozenTableStyling}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id} style={frozenHeaderRowStyling}>
                {headerGroup.headers.map((header) => {
                  return (
                    <Th
                      key={header.id}
                      textTransform="capitalize"
                      letterSpacing="normal"
                      padding="8px 16px 8px 16px"
                      borderBottomWidth="thin"
                      position="sticky"
                      top="0"
                      backgroundColor={colors.background[100]}
                      minW={header.column.getSize()}
                      maxW={header.column.getSize()}
                    >
                      <Flex alignItems="center">
                        {header.id === 'externalReceivableId' && (
                          <Checkbox
                            mr="16px"
                            isChecked={table.getIsAllRowsSelected()}
                            isIndeterminate={table.getIsSomeRowsSelected()}
                            onChange={() =>
                              dispatchRowSelection({
                                type: 'TOGGLE_ALL',
                                allRowIds,
                              })
                            }
                            isDisabled={jobInProgress}
                          />
                        )}
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody backgroundColor="white">
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id} padding="8px 16px" height="56px">
                    <Flex alignItems="center">
                      {cell.column.id === 'externalReceivableId' && (
                        <Checkbox
                          mr="16px"
                          isChecked={row.getIsSelected()}
                          onChange={() =>
                            dispatchRowSelection({
                              type: 'TOGGLE_ROW',
                              rowId: row.original.externalReceivableId,
                            })
                          }
                          isDisabled={jobInProgress}
                        />
                      )}
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Flex>
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};
